import { useState } from "react";
import { DateInput } from "@/components/DateInput";
import { toast } from "sonner";
import { CheckCircle, Circle, Plus, Trash, NotePencil, Robot, UserCircle, CalendarBlank } from "@phosphor-icons/react";

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

export function StepNotes({ stepId, stepTitle, initial, onSave }) {
  const [note, setNote] = useState(initial?.note || "");
  const [tasks, setTasks] = useState(initial?.tasks || []);
  const [newTask, setNewTask] = useState("");
  const [newAssignee, setNewAssignee] = useState("");
  const [newDue, setNewDue] = useState("");
  const [savedNote, setSavedNote] = useState(initial?.note || "");

  const persist = async (nextNote, nextTasks) => {
    try {
      await onSave(stepId, { note: nextNote, tasks: nextTasks });
      return true;
    } catch {
      toast.error("Couldn't save — try again");
      return false;
    }
  };

  const saveNote = async () => {
    if (note === savedNote) return;
    const ok = await persist(note, tasks);
    if (ok) {
      setSavedNote(note);
      toast.success("Note saved");
    }
  };

  const addTask = () => {
    const text = newTask.trim();
    if (!text) return;
    const next = [...tasks, { id: uid(), text, done: false, assignee: newAssignee.trim(), due_date: newDue }];
    setTasks(next);
    setNewTask("");
    setNewAssignee("");
    setNewDue("");
    persist(note, next);
  };

  const toggleTask = (tid) => {
    const next = tasks.map((t) => (t.id === tid ? { ...t, done: !t.done } : t));
    setTasks(next);
    persist(note, next);
  };

  const removeTask = (tid) => {
    const next = tasks.filter((t) => t.id !== tid);
    setTasks(next);
    persist(note, next);
  };

  const askAi = () => {
    window.dispatchEvent(
      new CustomEvent("bk-ask-ai", {
        detail: { text: `Explain this bookkeeping step in more detail for my business: "${stepTitle}".` },
      })
    );
  };

  return (
    <div className="mt-4 pt-4 border-t space-y-4" style={{ borderColor: "var(--bk-border)" }} data-testid={`stepnotes-${stepId}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold flex items-center gap-1.5" style={{ color: "var(--bk-success)" }}>
          <NotePencil size={15} weight="duotone" /> Your notes & tasks
        </span>
        <button onClick={askAi} data-testid={`ask-ai-${stepId}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }}>
          <Robot size={15} weight="duotone" /> Ask AI about this step
        </button>
      </div>

      <textarea
        data-testid={`note-input-${stepId}`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={saveNote}
        rows={2}
        placeholder="Add a private note for this business (e.g. 'Bank is Chase — waiting on Jan statement')…"
        className="w-full px-3 py-2.5 rounded-md border text-sm outline-none focus:ring-2 resize-y"
        style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
      />

      {tasks.length > 0 && (
        <div className="space-y-2">
          {tasks.map((t) => (
            <div key={t.id} data-testid={`task-${t.id}`} className="flex items-center gap-2.5 group">
              <button onClick={() => toggleTask(t.id)} data-testid={`task-toggle-${t.id}`} className="shrink-0">
                {t.done ? <CheckCircle size={20} weight="fill" color="var(--bk-success)" /> : <Circle size={20} color="var(--bk-disabled)" />}
              </button>
              <div className="flex-1 min-w-0">
                <span className="text-sm block" style={{ color: t.done ? "var(--bk-disabled)" : "var(--bk-text)", textDecoration: t.done ? "line-through" : "none" }}>
                  {t.text}
                </span>
                {(t.assignee || t.due_date) && (
                  <span className="flex items-center gap-2 text-xs mt-0.5" style={{ color: "var(--bk-text-2)" }}>
                    {t.assignee && <span className="inline-flex items-center gap-1"><UserCircle size={13} weight="duotone" />{t.assignee}</span>}
                    {t.due_date && <span className="inline-flex items-center gap-1"><CalendarBlank size={13} weight="duotone" />{t.due_date}</span>}
                  </span>
                )}
              </div>
              <button onClick={() => removeTask(t.id)} data-testid={`task-delete-${t.id}`} className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity" title="Delete task">
                <Trash size={16} weight="duotone" color="var(--bk-text-2)" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <input
            data-testid={`new-task-input-${stepId}`}
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTask(); } }}
            placeholder="Add a custom sub-task…"
            className="flex-1 px-3 py-2 rounded-md border text-sm outline-none focus:ring-2"
            style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
          />
          <button onClick={addTask} disabled={!newTask.trim()} data-testid={`add-task-${stepId}`} className="w-9 h-9 rounded-md flex items-center justify-center disabled:opacity-40" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }}>
            <Plus size={18} weight="bold" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid={`new-task-assignee-${stepId}`}
            value={newAssignee}
            onChange={(e) => setNewAssignee(e.target.value)}
            placeholder="Assign to (optional)"
            className="flex-1 px-3 py-1.5 rounded-md border text-xs outline-none focus:ring-2"
            style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
          />
          <DateInput
            
            data-testid={`new-task-due-${stepId}`}
            value={newDue}
            onChange={(e) => setNewDue(e.target.value)}
            className="px-3 py-1.5 rounded-md border text-xs outline-none focus:ring-2"
            style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }}
          />
        </div>
      </div>
    </div>
  );
}
